import { Fragment, RefObject, useEffect, useRef } from 'react';
import { Transition } from '@headlessui/react';
import { XIcon } from 'lucide-react';
import i18n from '@/settings/i18n';
import CountryList from './CountryList';

const t = i18n.init('shared');

const CountrySelectorDropdown = ({
  open,
  setOpen,
  triggerRef,
}: {
  open: boolean;
  setOpen: (state: boolean) => void;
  triggerRef?: RefObject<HTMLElement>;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      // Clicks on the trigger are handled by its own onClick (toggle), so
      // don't treat them as "outside" — otherwise it would close then reopen.
      if (
        !containerRef.current?.contains(target) &&
        !triggerRef?.current?.contains(target)
      ) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        // The search box takes focus on open; hand it back to the trigger so
        // keyboard users don't land on <body> once the dropdown unmounts.
        triggerRef?.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, setOpen, triggerRef]);

  const closeDropdown = () => {
    setOpen(false);
    triggerRef?.current?.focus();
  };

  return (
    <div ref={containerRef}>
      <Transition
        show={open}
        as={Fragment}
        enter="transition duration-150 ease-out"
        enterFrom="-translate-y-1 opacity-0"
        enterTo="translate-y-0 opacity-100"
        leave="transition duration-100 ease-in"
        leaveFrom="translate-y-0 opacity-100"
        leaveTo="-translate-y-1 opacity-0"
      >
        <div className="absolute right-0 top-[40px] z-50 w-[360px] overflow-hidden rounded-xl border bg-white p-3 shadow-lg">
          <div className="flex items-center justify-between px-3 pb-2">
            <h3 className="text-sm font-semibold">{t`Choose your country`}</h3>
            <button
              type="button"
              onClick={closeDropdown}
              aria-label={t`Close`}
              className="transition-transform duration-100 hover:scale-105"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
          <CountryList
            onClose={closeDropdown}
            autoFocusSearch
            listClassName="max-h-[min(460px,calc(100vh-300px))]"
          />
        </div>
      </Transition>
    </div>
  );
};

export default CountrySelectorDropdown;
